import {
  AccountBalanceOutlined,
  PersonAdd,
  PriceCheck,
  RequestPageOutlined,
  RequestQuoteOutlined,
  Settings,
  StickyNote2,
  SupervisedUserCircle,
  SyncAlt,
  TrendingUp,
  Workspaces,
} from '@mui/icons-material';
import { FC } from 'react';
import { Redirect, Switch } from 'react-router-dom';

import * as paths from 'adminConstants/routes';
import Vehicles from 'routes/LpPages/Vehicle/ListVehicles';
import CommonRoutes from './CommonRoutes';
import RestrictedRoute from './RestrictedRoute';

import { CheckCircle } from '@mui/icons-material';
import ApprovePrepayments from 'routes/LpPages/AccountingCalendar/ApprovePrepayments';
import ApproveTransactionRates from 'routes/LpPages/AccountingCalendar/ApproveTransactionRates';
import PostUnrealisedFXGains from 'routes/LpPages/AccountingCalendar/PostUnrealisedFXGains';
import CashHistoryDetail from 'routes/LpPages/AccountingCalendar/PostUnrealisedFXGains/CashHistoryDetail';
import PayablesOrReceivablesHistoryDetail from 'routes/LpPages/AccountingCalendar/PostUnrealisedFXGains/PayablesOrReceivablesHistoryDetail';
import PostUnrealisedFXGainsSuccess from 'routes/LpPages/AccountingCalendar/PostUnrealisedFXGains/Success';
import ReviewAccruals from 'routes/LpPages/AccountingCalendar/ReviewAccruals';
import BankReconciliation from 'routes/LpPages/BankReconciliation';
import AddNewBankAccount from 'routes/LpPages/BankReconciliation/AddNewBankAccount';
import ImportStatement from 'routes/LpPages/BankReconciliation/ImportStatement';
import ProcessBankAccount from 'routes/LpPages/BankReconciliation/ProcessBankAccount';
import AddEditCapitalAction from 'routes/LpPages/CapitalActions/AddEditCapitalAction';
import CapitalActionSuccess from 'routes/LpPages/CapitalActions/AddEditCapitalAction/Success';
import CreateCapitalActionNotice from 'routes/LpPages/CapitalActions/CreateNotice';
import ListCapitalActions from 'routes/LpPages/CapitalActions/ListCapitalActions';
import ListCommitments from 'routes/LpPages/Commitments/ListCommitments';
import ViewCommitment from 'routes/LpPages/Commitments/ViewCommitment';
import AddEditCompany from 'routes/LpPages/Company/AddEditCompany';
import ListCompanies from 'routes/LpPages/Company/ListCompanies';
import Equalisations from 'routes/LpPages/Equalisations';
import AddEqualisation from 'routes/LpPages/Equalisations/AddEqualisation';
import EqualisationsEditor from 'routes/LpPages/Equalisations/Editor';
import ProcessCloseSuccess from 'routes/LpPages/Equalisations/ProcessCloseSuccess';
import FeeManager from 'routes/LpPages/Fees/FeeManager';
import FirmSettings from 'routes/LpPages/FirmSettings';
import GeneralLedger from 'routes/LpPages/FundAccounting';
import FundSetup from 'routes/LpPages/FundSetup';
import AddEditIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/AddEditIncomeOrExpense';
import IncomeOrExpenseSuccess from 'routes/LpPages/IncomeAndExpenses/AddEditIncomeOrExpense/Success';
import ListIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/ListIncomeAndExpenses';
import ViewIncomeOrExpense from 'routes/LpPages/IncomeAndExpenses/ViewIncomeOrExpense';
import CreateInvestment from 'routes/LpPages/Investment/CreateInvestment';
import InvestmentSuccess from 'routes/LpPages/Investment/CreateInvestment/InvestmentSuccess';
import ListInvestments from 'routes/LpPages/Investment/ListInvestments';
import ViewInvestment from 'routes/LpPages/Investment/ViewInvestment';
import AddEditManualAction from 'routes/LpPages/ManualJournal/AddEditManualAction';
import ListManualActions from 'routes/LpPages/ManualJournal/ListManualActions';
import Onboarding from 'routes/LpPages/Onboarding';
import AddPartnerTransfer from 'routes/LpPages/PartnerTransfers/CreatePartnerTransfer';
import ListPartnerTransfers from 'routes/LpPages/PartnerTransfers/ListPartnerTransfers';
import UserManagement from 'routes/LpPages/UserManagement';
import AddVehicle from 'routes/LpPages/Vehicle/AddVehicle';
import ProfilePage from 'routes/Pages/Profile/LpProfile';
import Revaluations from '../LpPages/Company/Revaluations';
import Realisation from '../LpPages/Realisation/CreateRealisation';
import ListRealisations from '../LpPages/Realisation/ListRealisations';
import ViewRealisation from '../LpPages/Realisation/ViewRealisation';

const LpRoutes: FC = () => (
  <Switch>
    <Redirect path="/" exact to={paths.LP_ONBOARDING} />
    <RestrictedRoute
      component={Vehicles}
      path={paths.VEHICLES}
      breadcrumbs={['Vehicles']}
      icon={Workspaces}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddVehicle}
      path={paths.VEHICLE_ADD}
      breadcrumbs={[{ label: 'Vehicles', link: paths.VEHICLES }, 'Add Vehicle']}
      useErrorBoundary
    />
    <RestrictedRoute component={GeneralLedger} path={paths.GENERAL_LEDGER} />
    <RestrictedRoute
      component={FundSetup}
      path={paths.FUND_SETUP}
      breadcrumbs={['Fund Setup']}
      icon={PriceCheck}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ImportStatement}
      path={paths.IMPORT_STATEMENT_FILE}
      icon={AccountBalanceOutlined}
      breadcrumbs={[
        { label: 'Bank Reconciliation', link: paths.BANK_RECONCILIATION },
        'Import statement',
      ]}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddNewBankAccount}
      path={paths.ADD_BANK_ACCOUNT}
      icon={AccountBalanceOutlined}
      breadcrumbs={[
        { label: 'Bank Reconciliation', link: paths.BANK_RECONCILIATION },
        'Add bank account',
      ]}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ProcessBankAccount}
      path={paths.PROCESS_BANK_ACCOUNT}
      breadcrumbs={['Bank Reconciliation', 'Process bank account']}
      icon={AccountBalanceOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={BankReconciliation}
      path={paths.BANK_RECONCILIATION}
      icon={AccountBalanceOutlined}
      breadcrumbs={['Bank Reconciliation']}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_ADD}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'Add income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={IncomeOrExpenseSuccess}
      path={paths.INCOME_OR_EXPENSE_SUCCESS}
      breadcrumbs={['Income and expenses']}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_EDIT}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'Edit income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_LIST}
      breadcrumbs={['Income and expenses']}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewIncomeOrExpense}
      path={paths.INCOME_OR_EXPENSE_VIEW}
      breadcrumbs={[
        { label: 'Income and expenses', link: paths.INCOME_OR_EXPENSE_LIST },
        'View income or expense',
      ]}
      icon={SyncAlt}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CreateInvestment}
      path={paths.LP_INVESTMENT_CREATE}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Create investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CreateInvestment}
      path={paths.LP_INVESTMENT_EDIT}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Edit investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewInvestment}
      path={paths.LP_INVESTMENT_VIEW}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'View investment',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={InvestmentSuccess}
      path={paths.LP_INVESTMENT_SUCCESS}
      breadcrumbs={[
        { label: 'Investments', link: paths.LP_INVESTMENT_LIST },
        'Success',
      ]}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListInvestments}
      path={paths.LP_INVESTMENT_LIST}
      breadcrumbs={['Investments']}
      icon={TrendingUp}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListManualActions}
      path={paths.LP_MANUAL_ACTION_LIST}
      breadcrumbs={['Manual Journal']}
      icon={StickyNote2}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditManualAction}
      path={paths.LP_MANUAL_ACTION_CREATE}
      breadcrumbs={[
        { label: 'Manual Journal', link: paths.LP_MANUAL_ACTION_LIST },
        'Add Manual action',
      ]}
      icon={StickyNote2}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditCompany}
      path={paths.LP_COMPANY_ADD}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditCompany}
      path={paths.LP_COMPANY_EDIT}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCompanies}
      path={paths.LP_COMPANY_LIST}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Revaluations}
      path={paths.LP_REVALUATIONS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Realisation}
      path={paths.LP_REALISATION_CREATE}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListRealisations}
      path={paths.LP_REALISATION_LIST}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ViewRealisation}
      path={paths.LP_REALISATION_VIEW}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCapitalActions}
      path={paths.LP_CAPITAL_ACTION_LIST}
      breadcrumbs={['Capital actions']}
      icon={RequestQuoteOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEditCapitalAction}
      path={paths.LP_CAPITAL_ACTION_CREATE}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Create Capital action',
      ]}
      icon={RequestQuoteOutlined}
    />
    <RestrictedRoute
      component={CapitalActionSuccess}
      path={paths.LP_CAPITAL_ACTION_CREATE_SUCCESS}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Capital action added',
      ]}
      icon={RequestQuoteOutlined}
    />
    <RestrictedRoute
      component={AddEditCapitalAction}
      path={paths.LP_CAPITAL_ACTION_EDIT}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Edit Capital action',
      ]}
      icon={RequestQuoteOutlined}
    />
    <RestrictedRoute
      component={CreateCapitalActionNotice}
      path={paths.LP_CAPITAL_ACTION_NOTICE_CREATE}
      breadcrumbs={[
        { label: 'Capital actions', link: paths.LP_CAPITAL_ACTION_LIST },
        'Capital action notice',
      ]}
      icon={RequestQuoteOutlined}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ListCommitments}
      path={paths.COMMITMENTS}
      breadcrumbs={['Commitments']}
      icon={CheckCircle}
    />
    <RestrictedRoute
      component={ViewCommitment}
      path={paths.COMMITMENTS_VIEW}
      breadcrumbs={[{ label: 'Commitments', link: paths.COMMITMENTS }]}
      icon={CheckCircle}
    />
    <RestrictedRoute
      icon={RequestPageOutlined}
      breadcrumbs={['Management fees']}
      component={FeeManager}
      path={paths.FEE_MANAGER}
    />
    <RestrictedRoute
      component={ProcessCloseSuccess}
      path={paths.EQUALISATIONS_PROCESS_CLOSE_SUCCESS}
    />
    <RestrictedRoute
      component={EqualisationsEditor}
      path={paths.EQUALISATIONS_EDITOR}
      useErrorBoundary
    />
    <RestrictedRoute
      component={Equalisations}
      path={paths.EQUALISATIONS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={AddEqualisation}
      path={paths.EQUALISATIONS_ADD}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ApprovePrepayments}
      path={paths.APPROVE_PREPAYMENTS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ReviewAccruals}
      path={paths.REVIEW_ACCRUALS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={ApproveTransactionRates}
      path={paths.APPROVE_TRANSACTION_RATES}
      useErrorBoundary
    />
    <RestrictedRoute
      component={CashHistoryDetail}
      path={paths.UNREALISED_CASH_FX_HISTORY_DETAIL}
      useErrorBoundary
    />
    <RestrictedRoute
      component={PayablesOrReceivablesHistoryDetail}
      path={paths.UNREALISED_FX_HISTORY_DETAIL}
      useErrorBoundary
    />
    <RestrictedRoute
      component={PostUnrealisedFXGains}
      path={paths.POST_UNREALISED_FX_GAINS}
      useErrorBoundary
    />
    <RestrictedRoute
      component={PostUnrealisedFXGainsSuccess}
      path={paths.POST_UNREALISED_FX_GAINS_SUCCESS}
    />
    <RestrictedRoute
      component={Onboarding}
      path={paths.LP_ONBOARDING}
      useErrorBoundary
      breadcrumbs={['Onboarding']}
      icon={PersonAdd}
    />
    <RestrictedRoute
      component={ListPartnerTransfers}
      path={paths.PARTNER_TRANSFERS_LIST}
      exact
      useErrorBoundary
      breadcrumbs={['Partner Transfers']}
    />
    <RestrictedRoute
      component={AddPartnerTransfer}
      path={paths.PARTNER_TRANSFERS_CREATE}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Partner Transfers', link: paths.PARTNER_TRANSFERS_LIST },
        'Create Partner Transfer',
      ]}
    />
    <RestrictedRoute
      component={AddPartnerTransfer}
      path={paths.PARTNER_TRANSFERS_EDIT}
      useErrorBoundary
      breadcrumbs={[
        { label: 'Partner Transfers', link: paths.PARTNER_TRANSFERS_LIST },
        'Edit Partner Transfer',
      ]}
    />
    <RestrictedRoute component={ProfilePage} path={paths.LP_PROFILE} />
    <RestrictedRoute
      component={FirmSettings}
      path={paths.LP_FIRM_SETTINGS}
      breadcrumbs={['Firm settings']}
      icon={Settings}
      useErrorBoundary
    />
    <RestrictedRoute
      component={UserManagement}
      path={paths.USER_MANAGEMENT}
      breadcrumbs={['User management']}
      icon={SupervisedUserCircle}
      useErrorBoundary
    />
    {...CommonRoutes}
  </Switch>
);

export default LpRoutes;
